import { useCallback, type ChangeEvent, type ReactNode } from 'react'
import { type EntityId } from '@terros/common'
import { notifications } from '@mantine/notifications'
import { Switch } from '@mantine/core'
import { useToastError } from '@hooks'
import { useUpdatePreference } from '@front/model'
import { PreferenceInput } from './PreferenceInput'

type BooleanKeys =
  | 'autoAssignEvals'
  | 'blockCustomLocationDetailEdits'
  | 'canBeInMultipleTeams'
  | 'enableAccountManagement'
  | 'enableAreaManagement'
  | 'enableVisibilityRestrictions'
  | 'enerflo'
  | 'hideInactivePins'
  | 'limitByArea'
  | 'managerBasedTeams'
  | 'phonePitch'
  | 'requireClockInBeforeContact'
  | 'requireEvalToCanvass'
  | 'restrictSetterCloseEvents'
  | 'showCommercialProperties'
  | 'showMultiUnitDwellings'
  | 'sso'
  | 'taskLockout'
  | 'planLockout'

type Props = {
  preference: BooleanKeys
  targetId: EntityId
  disabled?: boolean
}

export const PreferenceSwitch = ({ preference, targetId, disabled }: Props): ReactNode => {
  const { update, isUpdating, error: updateError } = useUpdatePreference(preference, targetId)
  useToastError(updateError)

  const set = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      await update(event.target.checked)
      notifications.show({ message: 'Preference saved', color: 'green' })
    },
    [update]
  )

  return (
    <PreferenceInput targetId={targetId} preference={preference} isUpdating={isUpdating} inline>
      {({ data, disabled: isReadOnly }) => (
        <Switch checked={data.current ?? data.inherited} disabled={disabled || isReadOnly} flex={1} onChange={set} />
      )}
    </PreferenceInput>
  )
}
